import { useCallback, useMemo, useState } from "react";
import type { Patient } from "fhir/r5";
import type { EcommerceListAudience } from "../helpers/ecommerceListAudience";
import {
  buildConsentPatientRows,
  buildRegisteredClientRows,
  matchesPeopleRowSearch,
  type EcommercePeopleRow,
} from "../helpers/ecommercePeopleRow";
import { getPatientCpfKey } from "../helpers/ecommercePersonDisplay";
import type { EcommerceWhatsAppTarget } from "../helpers/resolveEcommerceWhatsAppRecipients";

export function useEcommercePeopleList(
  audience: EcommerceListAudience,
  patients: Patient[],
  persons: fhir5.Person[],
  searchTerm: string,
) {
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

  const consentCpfs = useMemo(() => {
    const set = new Set<string>();
    patients.forEach((patient) => {
      const cpf = getPatientCpfKey(patient);
      if (cpf) set.add(cpf);
    });
    return set;
  }, [patients]);

  const rows = useMemo(() => {
    if (audience === "consent_patients") {
      return buildConsentPatientRows(patients);
    }
    if (audience === "registered_clients") {
      return buildRegisteredClientRows(persons, consentCpfs);
    }
    return [] as EcommercePeopleRow[];
  }, [audience, patients, persons, consentCpfs]);

  const filteredRows = useMemo(
    () => rows.filter((row) => matchesPeopleRowSearch(row, searchTerm)),
    [rows, searchTerm],
  );

  const filteredKeys = useMemo(
    () => filteredRows.map((row) => row.key),
    [filteredRows],
  );

  const handleToggleRow = useCallback((key: string) => {
    setSelectedKeys((previous) =>
      previous.includes(key)
        ? previous.filter((selectedKey) => selectedKey !== key)
        : [...previous, key],
    );
  }, []);

  const handleToggleAll = useCallback(
    (checked: boolean) => {
      if (checked) {
        setSelectedKeys((previous) => [
          ...new Set([...previous, ...filteredKeys]),
        ]);
        return;
      }
      setSelectedKeys((previous) =>
        previous.filter((key) => !filteredKeys.includes(key)),
      );
    },
    [filteredKeys],
  );

  const clearSelection = useCallback(() => setSelectedKeys([]), []);

  const selectedFilteredCount = filteredKeys.filter((key) =>
    selectedKeys.includes(key),
  ).length;

  const selectedTargets = useMemo((): EcommerceWhatsAppTarget[] => {
    const selected = new Set(selectedKeys);
    return filteredRows
      .filter((row) => selected.has(row.key))
      .map((row) => ({ patient: row.patient, person: row.person }));
  }, [filteredRows, selectedKeys]);

  return {
    filteredRows,
    selectedKeys,
    selectedFilteredCount,
    selectedTargets,
    handleToggleRow,
    handleToggleAll,
    clearSelection,
  };
}
